import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Input,
  Table,
  Row,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import { useSelector } from 'react-redux';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { formatDate } from '../../constants/DateFormatter';
import {
  createAppConnection,
  joinModuleGroup,
  leaveModuleGroup,
  subscribeRealtimeUpdate,
  unsubscribeRealtimeUpdate,
} from '../../utils/signalRService';

const MODULE_NAME = 'Holiday';

const HolidayList = () => {
  const { user } = useSelector((state) => state.authState);
  const navigate = useNavigate();

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [holidayList, setHolidayList] = useState([]);
  const [search, setSearch] = useState('');

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const currentGroupRef = useRef({
    module: null,
    vendorId: null,
    branchId: null,
  });

  const onHandleAdd = () => navigate('/save-holiday');

  const onHandleEdit = (id) => {
    const selected = holidayList.find((x) => x.id === id);
    navigate('/save-holiday', { state: { ...(selected || {}) } });
  };

  const GetHolidayList = useCallback(
    async (searchText = '') => {
      try {
        const vendorId = selectionData.vendor_id || '';
        const branchId = selectionData.branch_id || '';

        const res = await apiService.get(
          `api/HolidayMaster/GetAllHoliday?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
            searchText || '',
          )}`,
        );

        if (res?.status === true) {
          setHolidayList(res.data || []);
        } else {
          alert(res?.message || 'Failed');
        }
      } catch (err) {
        console.error(err);
        alert('Something went wrong');
      }
    },
    [selectionData.vendor_id, selectionData.branch_id],
  );

  const onHandleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    GetHolidayList(val);
  };

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      GetHolidayList(search);
    }
  }, [
    selectionData.vendor_id,
    selectionData.branch_id,
    hasMultiBranch,
    hasSingleBranch,
    isSuperAdmin,
    isAdmin,
    GetHolidayList,
    search,
  ]);

  useEffect(() => {
    let mounted = true;

    const setupSignalR = async () => {
      try {
        await createAppConnection();

        const vendorId = selectionData.vendor_id || 0;
        const branchId = selectionData.branch_id || 0;

        const prev = currentGroupRef.current;

        if (
          prev.vendorId &&
          prev.branchId &&
          (String(prev.vendorId) !== String(vendorId) ||
            String(prev.branchId) !== String(branchId))
        ) {
          await leaveModuleGroup(prev.module, prev.vendorId, prev.branchId);
        }

        if (vendorId && branchId) {
          await joinModuleGroup(MODULE_NAME, vendorId, branchId);

          currentGroupRef.current = {
            module: MODULE_NAME,
            vendorId,
            branchId,
          };
        }

        unsubscribeRealtimeUpdate();

        await subscribeRealtimeUpdate((payload) => {
          if (!mounted) return;

          if (!payload) return;

          const isSameModule = String(payload.module || '') === MODULE_NAME;
          const isSameVendor =
            String(payload.vendorId || '') === String(vendorId || '');
          const isSameBranch =
            String(payload.branchId || '') === String(branchId || '');

          if (isSameModule && isSameVendor && isSameBranch) {
            console.log('Realtime update received:', payload);
            GetHolidayList(search);
          }
        });
      } catch (err) {
        console.error('SignalR error:', err);
      }
    };

    setupSignalR();

    return () => {
      mounted = false;
    };
  }, [
    selectionData.vendor_id,
    selectionData.branch_id,
    GetHolidayList,
    search,
  ]);

  useEffect(() => {
    return () => {
      unsubscribeRealtimeUpdate();

      const prev = currentGroupRef.current;
      if (prev?.vendorId && prev?.branchId) {
        leaveModuleGroup(prev.module, prev.vendorId, prev.branchId).catch(
          console.error,
        );
      }
    };
  }, []);

  return (
    <>
      <div className='text-right mb-2'>
        <Button color='primary' onClick={onHandleAdd}>
          <i className='bi bi-plus-circle' /> Add Holiday
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0'>
          <i className='bi bi-calendar-event mr-2' /> Holiday List
        </CardTitle>

        <div className='p-2'>
          <Input
            placeholder='Search Holiday...'
            value={search}
            onChange={onHandleSearch}
          />
        </div>

        <CardBody>
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Edit</th>
                <th>Date</th>
                <th>Name</th>
                <th>Type</th>
                <th>Applicable For</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {holidayList.length > 0 ? (
                holidayList.map((x, i) => (
                  <tr key={x.id}>
                    <td>{i + 1}</td>
                    <td>
                      <i
                        className='bi bi-pencil text-primary'
                        style={{ cursor: 'pointer' }}
                        onClick={() => onHandleEdit(x.id)}
                      />
                    </td>
                    <td>{formatDate(x.holiday_date)}</td>
                    <td>{x.holiday_name}</td>
                    <td>{x.holiday_type}</td>
                    <td>{x.applicable_for}</td>
                    <td>{x.is_active ? 'Active' : 'Inactive'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='7' className='text-center'>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default HolidayList;
